/**
 * Timezone-aware date utilities using the native Intl API - no external deps.
 *
 * Rationale: events happen in a specific location at a specific wall-clock time.
 * When the organiser types "11:00 AM" for an event in Malaga, that value must be
 * interpreted as 11:00 AM in the event's timezone (Europe/Madrid), regardless of
 * where the organiser's browser is. Storing the correct UTC instant requires
 * explicit timezone handling; relying on `new Date(string)` uses the browser's
 * local timezone which is usually wrong for the event.
 */

/**
 * Wall-clock time to instant comes from the one shared resolver in
 * @fitvent/types (packages/types/src/zoned-time.ts), which the database and
 * ticket-sales packages use too, so every surface reads a time on a clock
 * change day the same way, as Postgres does (#1119):
 *
 *   - zonedTimeToUtc('2026-04-16', '11:00', 'Europe/Madrid') gives
 *     2026-04-16T09:00:00Z (Madrid is UTC+2 in April);
 *   - a time the clocks go back through is the LATER of the two;
 *   - a time the clocks skip lands just after the gap;
 *   - startOfZonedDay is the first moment a date shows, for midnight as a
 *     day boundary.
 */
export {
  canonicalTimeZone,
  startOfZonedDay,
  wallClockMsToInstant,
  zonedTimeToUtc,
  zoneOffsetMs,
} from '@fitvent/types';

/**
 * Given a UTC Date and an IANA timezone, return the wall-clock date string
 * ('YYYY-MM-DD') and time string ('HH:mm') in that timezone.
 *
 * Useful for pre-filling an edit form with the correct local values.
 */
export function utcToZonedWallClock(
  utcDate: Date,
  timeZone: string
): { date: string; time: string } {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(utcDate);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '00';
  const hourRaw = parseInt(get('hour'), 10);
  const hour = String(hourRaw === 24 ? 0 : hourRaw).padStart(2, '0');
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${hour}:${get('minute')}`,
  };
}

/**
 * Parse an "HH:MM" wall-clock time string (as typed by an organiser) into a Date
 * encoding the time AS-IF it were UTC. Round-tripping through a `timestamp without
 * time zone` column then reading it back via `getUTCHours`/`getUTCMinutes` yields
 * the original wall-clock hour and minute regardless of the host's process timezone.
 *
 * Accepts either an ISO timestamp (passed straight through `new Date`) or "HH:MM".
 * Uses a fixed 1970-01-01 epoch anchor so the date portion is stable and tests are
 * deterministic; consumers only ever read the time portion.
 */
export function parseWallClockTime(value: string | undefined | null): Date | undefined {
  if (!value) return undefined;
  const direct = new Date(value);
  if (!isNaN(direct.getTime())) return direct;
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return undefined;
    return new Date(Date.UTC(1970, 0, 1, hours, minutes, 0, 0));
  }
  return undefined;
}

/**
 * Format a UTC Date in a given timezone using Intl options.
 * Never uses the server/browser's local timezone.
 */
export function formatInTimeZone(
  utcDate: Date | string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-GB'
): string {
  const d = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  return d.toLocaleString(locale, { ...options, timeZone });
}
